import {
	BeneficiaireTool,
	CadreRemboursement,
	Document,
	DroitPatient,
	EnumTypeOrdonnance,
	EtatScor,
	EtatServiceOrdonnance,
	Exoneration,
	MobilePatient,
	MobilePraticien,
	MutuelleAttestationPatient,
	NatureSupportDroits,
	Ordonnance,
	Patient,
	Praticien,
	PreferenceForCreationOrdo,
	ScorOption,
	SupportDroitsData,
	Traitement,
} from "@jack-sparrow-libraries/emotion-models";
import { OrdonnanceCreationAction, OrdonnanceCreationActionTypes } from "./ordonnance-creation.action";
import { EtatsOrdonnance } from "@featuresAY/ordonnance-web-ay/models/etats-ordonnance.model";
import { TypeBsi } from "@enums/type-bsi.enum";
import { OrdonnanceStep } from "@featuresAY/ordonnance-web-ay/models/ordonnance-step.model";
import { EtapeOrdonnanceEnum, OrdonnanceRapideOptionsEnum } from "features";
import { EtapeCreationOrdoWebAyEnum } from "@featuresAY/ordonnance-web-ay/enums/etape-creation-ordo-web-ay.enum";
import { DateUtils } from "@utils/DateUtils";
import { v4 as uuidv4 } from "uuid";
import { cloneDeep } from "lodash-es";
import { OrdonnanceSerieSoinsModeEnum } from "@enums/ordonnance-mode.enum";

export interface OrdonnanceCreationState {
	loading: boolean;
	loaded: boolean;
	saving: boolean;
	saved: boolean;
	scorSelect: ScorOption;
	scorDocuments: Document[];
	mobilePatient: MobilePatient;
	mobilePraticien: MobilePraticien;
	patient: Patient;
	praticien: Praticien;
	ordonnance: Ordonnance;
	etatsOrdonnance: EtatsOrdonnance;
	beneficiaireTool: BeneficiaireTool;
	preferenceForCreationOrdo: PreferenceForCreationOrdo;
	droitPatient: DroitPatient;
	mutuelle: MutuelleAttestationPatient;
	type: EnumTypeOrdonnance;
	datePrescription: Date;
	titre: string;
	natureSupportDroits: NatureSupportDroits;
	isCompatibleBsi: boolean;
	typeBsi: TypeBsi;
	traitement: Traitement;
	traitements: Traitement[];
	etapesOrdonnance: OrdonnanceStep[];
	currentEtape: EtapeOrdonnanceEnum;
	isEditTraitement: boolean;
	dateDerniereSeance: Date;
	ordonnanceDoublon: Ordonnance;
	modeSS: OrdonnanceSerieSoinsModeEnum;
	typeOrdoRapide: OrdonnanceRapideOptionsEnum;
	contextRemboursement: any; // form du contexte de facturation
	pageTitle: string;
}

export const ordonnanceCreationInitialState: OrdonnanceCreationState = {
	loading: false,
	loaded: false,
	saving: false,
	saved: false,
	scorSelect: ScorOption.none,
	scorDocuments: [],
	mobilePatient: null,
	mobilePraticien: null,
	patient: null,
	praticien: null,
	ordonnance: null,
	etatsOrdonnance: { cpsEtat: EtatServiceOrdonnance.INCONNU, adrEtat: EtatServiceOrdonnance.INCONNU },
	beneficiaireTool: null,
	preferenceForCreationOrdo: null,
	droitPatient: null,
	mutuelle: null,
	type: EnumTypeOrdonnance.ORDONNANCE,
	datePrescription: null,
	titre: null,
	natureSupportDroits: null,
	isCompatibleBsi: false,
	typeBsi: null,
	traitement: null,
	traitements: [],
	etapesOrdonnance: [
		{ nameEtape: EtapeOrdonnanceEnum.NUMERISATION, etat: EtapeCreationOrdoWebAyEnum.EN_COURS },
		{
			nameEtape: EtapeOrdonnanceEnum.INFO_PRESCRIPTION,
			etat: EtapeCreationOrdoWebAyEnum.A_FAIRE,
		},
		{
			nameEtape: EtapeOrdonnanceEnum.CONTEXTE_FACTURATION,
			etat: EtapeCreationOrdoWebAyEnum.A_FAIRE,
		},
		{ nameEtape: EtapeOrdonnanceEnum.SERIE_SOINS, etat: EtapeCreationOrdoWebAyEnum.A_FAIRE },
		{ nameEtape: EtapeOrdonnanceEnum.PLANNIFICATION, etat: EtapeCreationOrdoWebAyEnum.A_FAIRE },
	],
	currentEtape: EtapeOrdonnanceEnum.NUMERISATION,
	isEditTraitement: false,
	dateDerniereSeance: null,
	ordonnanceDoublon: null,
	modeSS: OrdonnanceSerieSoinsModeEnum.AJOUT,
	typeOrdoRapide: null,
	contextRemboursement: null,
	pageTitle: "Nouvelle ordonnance",
};

export function ordonnanceCreationReducer(
	state: OrdonnanceCreationState = ordonnanceCreationInitialState,
	action: OrdonnanceCreationAction,
) {
	switch (action.type) {
		case OrdonnanceCreationActionTypes.ResetOrdonnanceCreation: {
			return { ...ordonnanceCreationInitialState };
		}

		case OrdonnanceCreationActionTypes.AddScorOrdonnanceCreation: {
			return {
				...state,
				scorSelect: action.payload.scorSelect,
				scorDocuments: action.payload.scorDocuments,
			};
		}

		case OrdonnanceCreationActionTypes.SetEtatsOrdonnanceCreation: {
			return {
				...state,
				etatsOrdonnance: action.payload,
			};
		}

		case OrdonnanceCreationActionTypes.UpdateDatePrescriptionOrdonnanceCreation: {
			const ordonnance = { ...state.ordonnance };
			if (ordonnance) ordonnance.datePrescription = action.payload;
			return {
				...state,
				ordonnance,
				datePrescription: action.payload,
			};
		}

		case OrdonnanceCreationActionTypes.SetMobilePatientOrdonnanceCreation: {
			return {
				...state,
				mobilePatient: action.payload,
			};
		}

		case OrdonnanceCreationActionTypes.SetMobilePraticienOrdonnanceCreation: {
			return {
				...state,
				mobilePraticien: action.payload,
			};
		}

		case OrdonnanceCreationActionTypes.SetPatientAndMutuelleOrdonnanceCreation: {
			const ordonnance = { ...state.ordonnance };
			const patient = action.payload;
			if (ordonnance) {
				ordonnance.patient = patient;
				ordonnance.caisse = action.payload.caisse;
			}

			return {
				...state,
				ordonnance,
				patient: patient,
				mutuelle: patient.mutuelleAttestationPatient
					? { ...patient.mutuelleAttestationPatient, patientId: patient.id }
					: null,
			};
		}

		case OrdonnanceCreationActionTypes.SetPraticienOrdonnanceCreation: {
			const ordonnance = { ...state.ordonnance };
			if (ordonnance) {
				ordonnance.praticien = action.payload;
			}

			return {
				...state,
				ordonnance,
				praticien: action.payload,
			};
		}

		case OrdonnanceCreationActionTypes.InitOrdonnanceForPatient: {
			const ordonnance = { ...action.payload.ordonnance };
			ordonnance.patient = action.payload.patient;
			ordonnance.praticien = action.payload.praticien;
			ordonnance.titre = action.payload.titre ? action.payload.titre : "";
			ordonnance.natureSupportDroits = action.payload.natureSupportDroits;
			ordonnance.etatScor = EtatScor.SCOR_TO_BE_COMPLETED;
			ordonnance.mutuelleAttestationPatient = null;
			// AY-10464 : sans supportDroitsDataId, on laisse supportDroitsData a null (et non {}),
			// sinon le back rejette le save (getSupportFromOrdonnance attend null pour le support papier).
			const initSupportDroitsDataId = action.payload?.droitPatient?.supportDroitsAMO?.supportDroitsDataId;
			ordonnance.supportDroitsData = initSupportDroitsDataId
				? ({ id: initSupportDroitsDataId } as SupportDroitsData)
				: null;
			ordonnance.dateDebut = null;

			return {
				...state,
				saving: false,
				saved: true,
				datePrescription: action.payload.datePrescription,
				titre: action.payload.titre ? action.payload.titre : "",
				natureSupportDroits: action.payload.natureSupportDroits,
				ordonnance: ordonnance,
				preferenceForCreationOrdo: action.payload.preferenceForCreationOrdo,
				droitPatient: action.payload.droitPatient,
				mutuelle: action.payload.mutuelle
					? { ...action.payload.mutuelle, patientId: action.payload.patient.id }
					: null,
				patient: action.payload.patient,
				praticien: action.payload.praticien,
			};
		}

		case OrdonnanceCreationActionTypes.SetTypeOrdonnanceCreation: {
			return {
				...state,
				type: action.payload,
			};
		}

		case OrdonnanceCreationActionTypes.SetOrdonnanceCreation: {
			return {
				...state,
				ordonnance: action.payload,
			};
		}

		case OrdonnanceCreationActionTypes.SetMutuelleAtttestationOrdonnanceCreation: {
			return {
				...state,
				mutuelle: action.payload
					? { ...action.payload, patientId: action.payload.patientId ?? state.patient?.id }
					: null,
			};
		}

		case OrdonnanceCreationActionTypes.UpdateTitreAndNatureDroitOrdonnanceCreation: {
			const ordonnance = { ...state.ordonnance };
			ordonnance.titre = action.payload.titre;
			ordonnance.natureSupportDroits = action.payload.natureDroits;

			return {
				...state,
				ordonnance,
				titre: action.payload.titre,
				natureSupportDroits: action.payload.natureDroits,
			};
		}

		case OrdonnanceCreationActionTypes.UpdateBeneficiaireToolAndDroitPatientOrdonnanceCreation: {
			// AY-10464 : null si aucun id resolu (sinon { id: undefined } => 500 cote back).
			const supportDroitsDataId =
				action.payload.supportDroitData?.id ??
				action.payload?.droitPatient?.supportDroitsAMO?.supportDroitsDataId;
			return {
				...state,
				beneficiaireTool: action.payload.beneficiaireTool,
				droitPatient: action.payload.droitPatient,
				ordonnance: {
					...state.ordonnance,
					supportDroitsData: supportDroitsDataId ? ({ id: supportDroitsDataId } as SupportDroitsData) : null,
				},
			};
		}
		case OrdonnanceCreationActionTypes.SetBeneficiaireToolOrdonnanceCreation: {
			return {
				...state,
				beneficiaireTool: action.payload,
			};
		}

		case OrdonnanceCreationActionTypes.UpdateOrdonnanceForBeneficiaireOrdonnanceEditionSuccess: {
			// AY-10464 : en edition, null si pas de support reel (CAS/papier) sinon le back renvoie 500
			// "SupportDroitsData non renseigne" (getSupportFromOrdonnance attend null -> support papier).
			const supportDroitsDataId = action.payload.supportDroitData?.id;
			return {
				...state,
				droitPatient: action.payload.droitPatient,
				ordonnance: {
					...state.ordonnance,
					supportDroitsData: supportDroitsDataId ? ({ id: supportDroitsDataId } as SupportDroitsData) : null,
				},
			};
		}

		case OrdonnanceCreationActionTypes.UpdateOrdonnanceForBeneficiaireOrdonnanceCreation: {
			const ordonnance = { ...state.ordonnance };
			if (action.payload.beneficiaireTool.isALD) {
				action.payload.beneficiaireTool.contexteFacturation.codeExoneration = Exoneration.ALD;
			}
			ordonnance.contexteFacturation = action.payload.beneficiaireTool.contexteFacturation;

			if (
				!action.payload.changeChoixTiersPayant &&
				action.payload.beneficiaireTool.contexteFacturation.choixTiersPayant ===
					CadreRemboursement.TP_AMO_AMC &&
				action.payload.beneficiaireTool.caisse
			) {
				ordonnance.contexteFacturation.choixTiersPayant = CadreRemboursement.TP_AMO;
			} else {
				ordonnance.contexteFacturation.choixTiersPayant = CadreRemboursement.TP_HORS;
			}

			ordonnance.datePrescription = action.payload.beneficiaireTool.datePrescription;
			ordonnance.caisse = action.payload.beneficiaireTool.caisse;

			return {
				...state,
				ordonnance,
			};
		}

		case OrdonnanceCreationActionTypes.SetTitreOrdonnanceCreation: {
			const ordonnance = { ...state.ordonnance };
			ordonnance.titre = action.payload;
			return {
				...state,
				ordonnance,
				titre: action.payload,
			};
		}

		case OrdonnanceCreationActionTypes.SetBsiOrdonnanceCreation: {
			return {
				...state,
				ordonnance: {
					...state.ordonnance,
					bsi: action.payload,
				},
			};
		}

		case OrdonnanceCreationActionTypes.SetBsiTypeOrdonnanceCreation: {
			return {
				...state,
				typeBsi: action.payload,
			};
		}

		case OrdonnanceCreationActionTypes.SetBsiCompatibilityOrdonnanceCreation: {
			return {
				...state,
				isCompatibleBsi: action.payload,
			};
		}

		case OrdonnanceCreationActionTypes.SetTraitementOrdonnanceCreation: {
			return {
				...state,
				traitement: action.payload,
			};
		}

		case OrdonnanceCreationActionTypes.ResetTraitementsOrdonnanceCreation: {
			const ordonnance = { ...state.ordonnance };
			if (ordonnance.traitements) {
				ordonnance.traitements = [];
			}

			return {
				...state,
				ordonnance,
				traitement: null,
				traitements: [],
			};
		}

		case OrdonnanceCreationActionTypes.SetCurrentEtapeOrdonnanceCreation: {
			return {
				...state,
				currentEtape: action.payload,
			};
		}

		case OrdonnanceCreationActionTypes.SetEtapeOrdonnanceCreation: {
			return {
				...state,
				currentEtape:
					action.payload.find((e) => e.etat === EtapeCreationOrdoWebAyEnum.EN_COURS)?.nameEtape ??
					EtapeOrdonnanceEnum.RECAP,
				etapesOrdonnance: action.payload,
			};
		}

		case OrdonnanceCreationActionTypes.GoNextEtapeOrdonnanceCreation: {
			const etapeToEnd = state.etapesOrdonnance.find((e) => e.etat === EtapeCreationOrdoWebAyEnum.EN_COURS);
			const etapeToStart = state.etapesOrdonnance.find((e) => e.etat === EtapeCreationOrdoWebAyEnum.A_FAIRE);
			const etapesOrdonnance = cloneDeep(state.etapesOrdonnance);
			return {
				...state,
				currentEtape: etapeToStart?.nameEtape ?? EtapeOrdonnanceEnum.RECAP,
				etapesOrdonnance: etapesOrdonnance.map((e) => {
					if (e.nameEtape === etapeToEnd.nameEtape) {
						return { ...e, etat: EtapeCreationOrdoWebAyEnum.FINI };
					}
					if (e.nameEtape === etapeToStart?.nameEtape) {
						return { ...e, etat: EtapeCreationOrdoWebAyEnum.EN_COURS };
					}
					return e;
				}),
			};
		}

		case OrdonnanceCreationActionTypes.EndAllEtapOrdonnanceCreation: {
			const etapesOrdonnance = cloneDeep(state.etapesOrdonnance);
			return {
				...state,
				etapesOrdonnance: etapesOrdonnance.map((e) => {
					return { ...e, etat: EtapeCreationOrdoWebAyEnum.FINI };
				}),
			};
		}

		case OrdonnanceCreationActionTypes.GoBackEtapeOrdonnanceCreation: {
			const etapeToRestart = state.etapesOrdonnance.find((e) => e.etat === EtapeCreationOrdoWebAyEnum.EN_COURS);
			const etapeToStart = [...state.etapesOrdonnance]
				.reverse()
				.find((e) => e.etat === EtapeCreationOrdoWebAyEnum.FINI);
			const etapesOrdonnance = cloneDeep(state.etapesOrdonnance);

			return {
				...state,
				currentEtape: etapeToStart?.nameEtape,
				etapesOrdonnance: etapesOrdonnance.map((e) => {
					if (e.nameEtape === etapeToRestart?.nameEtape) {
						return { ...e, etat: EtapeCreationOrdoWebAyEnum.A_FAIRE };
					}
					if (e.nameEtape === etapeToStart.nameEtape) {
						return { ...e, etat: EtapeCreationOrdoWebAyEnum.EN_COURS };
					}
					return e;
				}),
			};
		}

		case OrdonnanceCreationActionTypes.BackFromRecapOrdonnanceCreation: {
			const etapeToStart = state.etapesOrdonnance.find(
				(e) => e.nameEtape === EtapeOrdonnanceEnum.CONTEXTE_FACTURATION,
			);
			const etapesOrdonnance = cloneDeep(state.etapesOrdonnance);

			return {
				...state,
				currentEtape: etapeToStart?.nameEtape,
				etapesOrdonnance: etapesOrdonnance.map((e) => {
					if (e.nameEtape === etapeToStart.nameEtape) {
						return { ...e, etat: EtapeCreationOrdoWebAyEnum.EN_COURS };
					}
					return e;
				}),
			};
		}

		case OrdonnanceCreationActionTypes.StartEtapeOrdonnanceCreation: {
			const etapesOrdonnance = cloneDeep(state.etapesOrdonnance);
			return {
				...state,
				currentEtape: action.payload,
				etapesOrdonnance: etapesOrdonnance.map((e) =>
					e.nameEtape === action.payload ? { ...e, etat: EtapeCreationOrdoWebAyEnum.EN_COURS } : e,
				),
			};
		}

		case OrdonnanceCreationActionTypes.EndEtapesOrdonnanceCreation: {
			const etapesOrdonnance = cloneDeep(state.etapesOrdonnance);
			return {
				...state,
				etapesOrdonnance: etapesOrdonnance.map((e) =>
					action.payload.find((name) => name === e.nameEtape)
						? { ...e, etat: EtapeCreationOrdoWebAyEnum.FINI }
						: e,
				),
			};
		}

		case OrdonnanceCreationActionTypes.ResetEtapesOrdonnanceCreation: {
			const etapesOrdonnance = cloneDeep(state.etapesOrdonnance);
			return {
				...state,
				etapesOrdonnance: etapesOrdonnance.map((e) =>
					action.payload.find((name) => name === e.nameEtape)
						? { ...e, etat: EtapeCreationOrdoWebAyEnum.A_FAIRE }
						: e,
				),
			};
		}

		case OrdonnanceCreationActionTypes.RedoEtapesSerieSoinOrdonnanceCreation: {
			const etapesOrdonnance = cloneDeep(state.etapesOrdonnance);
			return {
				...state,
				currentEtape: EtapeOrdonnanceEnum.SERIE_SOINS,
				etapesOrdonnance: etapesOrdonnance.map((e) => {
					if (e.nameEtape === EtapeOrdonnanceEnum.SERIE_SOINS) {
						return { ...e, etat: EtapeCreationOrdoWebAyEnum.EN_COURS };
					}
					if (e.nameEtape === EtapeOrdonnanceEnum.PLANNIFICATION) {
						return { ...e, etat: EtapeCreationOrdoWebAyEnum.A_FAIRE };
					}
					return e;
				}),
			};
		}

		case OrdonnanceCreationActionTypes.ResetHardOriginEtapeOrdonnanceCreation: {
			return {
				...state,
				etapesOrdonnance: ordonnanceCreationInitialState.etapesOrdonnance,
			};
		}

		case OrdonnanceCreationActionTypes.SetEditTraitementOrdonnanceCreation: {
			return {
				...state,
				isEditTraitement: action.payload,
			};
		}

		case OrdonnanceCreationActionTypes.SetActeIsoleOrdonnanceCreation: {
			return {
				...state,
				ordonnance: { ...state.ordonnance, acteIsole: action.payload, dateDebut: DateUtils.todayWithoutTime() },
				type: action.payload ? EnumTypeOrdonnance.ACTEISOLE : EnumTypeOrdonnance.ORDONNANCE,
			};
		}

		case OrdonnanceCreationActionTypes.UpdateTraitementsOrdonnanceCreation: {
			let allTraitements = [];
			if (state.isEditTraitement) {
				allTraitements = cloneDeep(state.traitements);
				const currentTraitementsForUpdate = action.payload;
				currentTraitementsForUpdate.forEach((tr) => {
					const index = allTraitements.findIndex((t) => t.uuid === tr.uuid);
					if (index > -1) {
						allTraitements[index] = tr;
					}
				});
			} else {
				allTraitements = action.payload;
			}
			return { ...state, traitements: allTraitements };
		}

		case OrdonnanceCreationActionTypes.AddTraitementOrdonnanceCreation: {
			return { ...state, traitements: [...state.traitements, ...action.payload] };
		}

		case OrdonnanceCreationActionTypes.DeleteTraitementOrdonnanceCreation: {
			const curretnOrdo = cloneDeep(state.ordonnance);
			const traitementToDelete = action.payload;
			const indexToDelete = curretnOrdo?.traitements
				? curretnOrdo.traitements.findIndex((tr) => tr.uuid === traitementToDelete.uuid)
				: -1;
			if (indexToDelete > -1) {
				curretnOrdo.traitements.splice(indexToDelete, 1);
			}

			return {
				...state,
				ordonnance: curretnOrdo,
				traitements: curretnOrdo.traitements,
			};
		}

		case OrdonnanceCreationActionTypes.InitTraitementsOrdonnanceCreation: {
			const ordonnance = cloneDeep(action.payload);
			for (const traitement of ordonnance.traitements) {
				traitement.uuid = uuidv4();
			}
			const traitements = [...ordonnance.traitements];
			return {
				...state,
				ordonnance,
				traitements,
				traitement: null,
				loaded: true,
				loading: false,
			};
		}

		case OrdonnanceCreationActionTypes.SaveOrdonnanceCreation: {
			const traitements = cloneDeep(action.payload.traitements);
			for (const traitement of traitements) {
				traitement.uuid = uuidv4();
			}

			return {
				...state,
				ordonnance: action.payload,
			};
		}

		case OrdonnanceCreationActionTypes.EndOfOrdonnanceCreation: {
			return {
				...state,
				saving: false,
				saved: true,
			};
		}

		case OrdonnanceCreationActionTypes.SetModeSerieSoinsOrdonnanceCreation: {
			return {
				...state,
				modeSS: action.payload,
			};
		}

		case OrdonnanceCreationActionTypes.SetTypeOrdoRapideOrdonnanceCreation: {
			return {
				...state,
				typeOrdoRapide: action.payload,
			};
		}

		case OrdonnanceCreationActionTypes.SetContextRemboursementOrdonnanceCreation: {
			return {
				...state,
				contextRemboursement: action.payload,
			};
		}
		case OrdonnanceCreationActionTypes.ResetContextRemboursementOrdonnanceCreation: {
			const currentOrdo = { ...state.ordonnance };
			currentOrdo.contexteFacturation = null;
			return {
				...state,
				contextRemboursement: null,
				ordonnance: currentOrdo,
			};
		}

		case OrdonnanceCreationActionTypes.SetModeEditOrdonnanceCreation: {
			const currentOrdo = { ...state.ordonnance };
			return {
				...state,
				traitements: currentOrdo && action.payload ? currentOrdo.traitements : [],
			};
		}

		case OrdonnanceCreationActionTypes.SetPageTitleOrdonnanceCreation: {
			return {
				...state,
				pageTitle: action.payload,
			};
		}

		default:
			return state;
	}
}
